import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Space, Typography, message } from "antd";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { collection, getDocs, where, query } from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { db } from "../firebase/firebase";

const { Title, Text } = Typography;

const Eterna = () => {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [medecins, setMedecins] = useState([]);

  // Fetch all patients
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const patientsSnapshot = await getDocs(collection(db, "patients"));
        const patientsList = patientsSnapshot.docs.map((doc) => ({
          key: doc.id,
          ...doc.data(),
        }));
        // Only keep patients with eternaCommandDate
        setPatients(patientsList.filter(p => p.eternaCommandDate));
      } catch (error) {
        console.error("Error fetching patients:", error);
      }
    };
    fetchPatients();
  }, []);

  // Fetch all medecins
  useEffect(() => {
    const fetchMedecins = async () => {
      try {
        const medecinsSnapshot = await getDocs(collection(db, "medecins"));
        const medecinsList = medecinsSnapshot.docs.map((doc) => ({
          key: doc.id,
          ...doc.data(),
        }));
        setMedecins(medecinsList);
      } catch (error) {
        console.error("Error fetching medecins:", error);
      }
    };
    fetchMedecins();
  }, []);

  // Download function for Eterna files
  const downloadEternaFile = async (url, filename) => {
    if (!url) {
      message.error('No file URL provided');
      return;
    }

    message.loading({ content: 'Preparing download...', key: 'download', duration: 0 });

    try {
      const storage = getStorage();
      const storageRef = ref(storage, url);
      const downloadURL = await getDownloadURL(storageRef);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadURL;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success({ content: `${filename} downloaded successfully!`, key: 'download', duration: 2 });
    } catch (error) {
      message.error({ content: 'Download failed', key: 'download', duration: 2 });
    }
  };

  const showPatientDetails = (patient) => {
    setSelectedPatient(patient);
    setModalVisible(true);
  };

  const columns = [
    {
      title: "Patient ID",
      dataIndex: "key",
      key: "key",
      width: 120,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Surname",
      dataIndex: "surname",
      key: "surname",
    },
    {
      title: "Assigned Doctor",
      key: "assignedDoctor",
      render: (_, record) => {
        // Find the doctor whose uid matches the patient's userId
        const medecin = medecins.find(m => m.uid === record.userId);
        return medecin ? medecin.name : 'N/A';
      }
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button 
          type="primary" 
          icon={<EyeOutlined />}
          onClick={() => showPatientDetails(record)}
        >
          View Eterna Files
        </Button>
      ),
    },
  ];

  const EternaFilesModal = () => {
    if (!selectedPatient) return null;

    return (
      <Modal
        title={`Eterna Files - ${selectedPatient.name} ${selectedPatient.surname || ''}`}
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedPatient(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setModalVisible(false);
            setSelectedPatient(null);
          }}>
            Close
          </Button>
        ]}
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong>Patient ID:</Text> {selectedPatient.key}
          </div>
          <div>
            <Text strong>Name:</Text> {selectedPatient.name} {selectedPatient.surname || ''}
          </div>
          
          <div style={{ marginTop: '20px' }}>
            <Title level={4}>Eterna Files</Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              {selectedPatient.eterna && selectedPatient.eterna.img1 && (
                <div style={{ 
                  padding: '12px', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Text>Scan 1</Text>
                  <Button 
                    type="primary" 
                    icon={<DownloadOutlined />}
                    onClick={() => downloadEternaFile(selectedPatient.eterna.img1, `eterna_scan1_${selectedPatient.key}.stl`)}
                  >
                    Download
                  </Button>
                </div>
              )}
              
              {selectedPatient.eterna && selectedPatient.eterna.img2 && (
                <div style={{ 
                  padding: '12px', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Text>Scan 2</Text>
                  <Button 
                    type="primary" 
                    icon={<DownloadOutlined />}
                    onClick={() => downloadEternaFile(selectedPatient.eterna.img2, `eterna_scan2_${selectedPatient.key}.stl`)}
                  >
                    Download
                  </Button>
                </div>
              )}
              
              {(!selectedPatient.eterna || (!selectedPatient.eterna.img1 && !selectedPatient.eterna.img2)) && (
                <Text type="secondary">No Eterna files available for this patient.</Text>
              )}
            </Space>
          </div>
        </Space>
      </Modal>
    );
  };

  return (
    <div>
      <Title level={3}>Eterna Patients</Title>
      <Table 
        dataSource={patients} 
        columns={columns} 
        rowKey="key"
        pagination={{ pageSize: 10 }}
      />
      <EternaFilesModal />
    </div>
  );
};

export default Eterna; 